import type { StatefulDiaryT } from "../../../types/database/diaries";
import {
	combineObservationEntries,
	combineTrackerEntries,
} from "./flattenEntriesView";
// Define a base type for all log entries
interface BaseLogT {
	type: string;
	log_id: string;
	date: string;
	[key: string]: unknown;
}

export function collateDiaryHistory(diary: StatefulDiaryT) {
	// Flatten the diary into a single array
	const actions: BaseLogT[] = diary.actions_view?.length
		? (diary.actions_view
				.map((action) => {
					if (!action.action_id || !action.action_time) return null;
					return {
						...action,
						type: "action",
						log_id: action.action_id,
						date: action.action_time,
						action_time: action.action_time, // Keep original field for reference
					};
				})
				.filter(Boolean) as BaseLogT[])
		: [];

	// Ensure entries have all required EntryViewT fields
	const entries = combineTrackerEntries(diary)?.length
		? combineTrackerEntries(diary)
				.filter((item) => item.entry_id) // Filter out entries without entry_id
				.map((item) => ({
					...item,
					type: "entry",
					log_id: item.entry_id as string,
					date: item.calculated_entry_time,
					calculated_entry_time: item.calculated_entry_time, // Keep original field for reference
					entry_id: item.entry_id,
					entry_values: item.entry_values || [],
					created_at: item.created_at || null,
					diary_id: item.diary_id || null,
					entry_reference_date: item.entry_reference_date || null,
					entry_time: item.entry_time || null,
					last_edited: item.last_edited || null,
					observation_id: item.observation_id || null,
					schedule_id: item.schedule_id || null,
					schedule_index: item.schedule_index || null,
					time_unit: item.time_unit || null,
					timing_interval: item.timing_interval || null,
					timing_label: item.timing_label || null,
					tracker_id: item.tracker_id || null,
				}))
		: [];

	// Ensure events have all required ExtendedEventViewT fields
	const events = diary.events_view?.length
		? (diary.events_view
				.map((event) => {
					if (!event.event_id || !event.event_time) return null;
					return {
						...event,
						type: "event",
						log_id: event.event_id,
						date: event.event_time,
						event_time: event.event_time, // Keep original field for reference
						event_id: event.event_id,
						event_name: event.event_name || null,
						organisation_id: event.organisation_id || null,
						diary_id: event.diary_id || null,
						shared_attributes: event.shared_attributes || null,
						injections_view: event.injections_view || [],
						surgeries_view: event.surgeries_view || [],
						educations: event.educations || [], // Fixed property name
					};
				})
				.filter(Boolean) as BaseLogT[])
		: [];

	// Ensure observation entries have all required EntryViewT fields
	const observationEntries = combineObservationEntries(diary)?.length
		? combineObservationEntries(diary)
				.filter((item) => item.entry_id) // Filter out entries without entry_id
				.map((item) => ({
					...item,
					type: "observation",
					log_id: item.entry_id as string,
					date: item.calculated_entry_time,
					calculated_entry_time: item.calculated_entry_time, // Keep original field for reference
					entry_id: item.entry_id,
					entry_values: item.entry_values || [],
					created_at: item.created_at || null,
					diary_id: item.diary_id || null,
					entry_reference_date: item.entry_reference_date || null,
					entry_time: item.entry_time || null,
					last_edited: item.last_edited || null,
					observation_id: item.observation_id || null,
					schedule_id: item.schedule_id || null,
					schedule_index: item.schedule_index || null,
					time_unit: item.time_unit || null,
					timing_interval: item.timing_interval || null,
					timing_label: item.timing_label || null,
					tracker_id: item.tracker_id || null,
				}))
		: [];

	const flattenedDiary = [
		...actions,
		...entries,
		...events,
		...observationEntries,
	];

	// Sort the array using the common date field in ascending order (oldest first)
	const sortedDiary = flattenedDiary.sort((a, b) => {
		const aDate = new Date(a.date);
		const bDate = new Date(b.date);
		return aDate.getTime() - bDate.getTime(); // Sort in ascending order (oldest first)
	});

	return sortedDiary;
}
